<script module lang="ts">
	import type { PriceCategory } from '$lib/components/map/markers/markerContent/types';
	import type { PoiMarkerTemplateProps } from '$lib/components/map/markers/types';
	import type { PartnerPoiType, PoiType } from '$lib/components/pois/types';

	const mapPoiTypeWithProps: Record<Exclude<PoiType, PartnerPoiType>, PoiMarkerTemplateProps> = {
		AccommodationPOI: {
			scheme: 'hotel',
			recommended: false,
			icon: 'hotel',
		},
		AccommodationMichelinPOI: {
			scheme: 'hotelMichelin',
			recommended: true,
			icon: 'hotel',
		},
		RestaurantPOI: {
			scheme: 'restaurant',
			recommended: false,
			icon: 'restaurant',
		},
		RestaurantMichelinPOI: {
			scheme: 'restaurantMichelin',
			recommended: true,
			icon: 'restaurant',
		},
		TourismPOI: {
			scheme: 'tourism',
			recommended: true,
			icon: 'tourism',
		},
		ParkingPOI: {
			scheme: 'parking',
			recommended: false,
			icon: 'parking',
		},
		ServiceGasStationPOI: {
			scheme: 'energy',
			recommended: false,
			icon: 'energy-pump',
		},
		ServiceEVPOI: {
			scheme: 'electric',
			recommended: false,
			icon: 'flash',
		},
		ServicePOI: {
			scheme: 'energy',
			recommended: false,
			icon: 'energy-plug',
		},
		ServiceRestStopPOI: {
			scheme: 'rest',
			recommended: false,
			icon: 'rest-stop',
		},
	};

	const mapPriceCategoryToZIndex: Record<Exclude<PriceCategory, null>, string> = {
		LOW: 'z-float',
		MEDIAN: 'z-hover',
		HIGH: 'z-infront',
	};
</script>

<script lang="ts">
	import type { DotColor } from '$lib/components/map/markers/markerContent/types';
	import type {
		AccommodationMichelinPoiData,
		AccommodationPoiData,
		FuelPrices,
		FuelType,
		GasStationPoiData,
		PoiData,
	} from '$lib/components/pois/types';
	import type { Price } from '$lib/components/types';
	import type { ComponentProps } from 'svelte';
	import type { ClassValue } from 'svelte/elements';
	import Icon from '$lib/components/icons/Icon.svelte';
	import RepeatIcons from '$lib/components/icons/RepeatIcons.svelte';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import MarkerTemplate from '$lib/components/map/markers/markerContent/MarkerTemplate.svelte';
	import { usePopupState } from '$lib/components/map/popup/usePopupState.svelte';
	import { mapPriceCategory } from '$lib/components/pois/card/constant';
	import { isAccommodationMichelinPoi, isAnyAccommodationPoi } from '$lib/components/pois/utils';
	import { getSponsoredPoiCategoryConfigByPoi, isAnyPoiPartner } from '$lib/constants/sponsoredPoi';
	import { getLanguageCode } from '$lib/i18n/language';
	import { isMobile } from '$lib/services/mediaQuery/screenSize';
	import { getSearchParamsStore } from '$lib/services/urls/searchParams';
	import { formatPrice } from '$lib/utils/formatPrice';
	import { watch } from 'runed';
	import { debounce } from 'throttle-debounce';

	interface Props {
		poi: PoiData;
		detailedView?: boolean;
		tag?: string;
		iconTag?: string;
		isSelectedLocation?: boolean;
	}

	const {
		poi,
		detailedView = false,
		tag = 'div',
		iconTag = 'div',
		isSelectedLocation = false,
	}: Props = $props();

	let dotColor: DotColor | undefined = $state();
	let fuelPrice: Price | undefined = $state();
	let classes: ClassValue | undefined = $state();
	const { setPopupState, getPopupState } = usePopupState();

	const DEFAULT_DEBOUNCE_TIME = 250;

	const searchParams = getSearchParamsStore();
	const { hoveredPoiId } = getMapLayoutContext();

	const isHovered = $derived($hoveredPoiId === poi.id);
	const hasPopupOpen = $derived(getPopupState().open);

	function getDotColor(priceCategory: PriceCategory | undefined): DotColor | undefined {
		return (priceCategory && mapPriceCategory[priceCategory]) || undefined;
	}

	function getGasStationZIndex(priceCategory: PriceCategory | undefined): string | undefined {
		return priceCategory ? mapPriceCategoryToZIndex[priceCategory] : undefined;
	}

	const isServiceGasStationPOI = $derived(poi.__typename === 'ServiceGasStationPOI');

	function onGasStationFilterChange(gasStationFilter: FuelType[] | undefined) {
		if (isServiceGasStationPOI && detailedView && gasStationFilter?.length === 1) {
			const fuelType = gasStationFilter[0];
			const prices = ((poi as GasStationPoiData)?.fuels?.prices as FuelPrices) || [];

			const { price, priceCategory }
				= prices.find(({ filterOptionId }) => filterOptionId === fuelType) || {};

			fuelPrice = price && { ...price };
			dotColor = getDotColor(priceCategory);
			classes = getGasStationZIndex(priceCategory);
		} else {
			fuelPrice = undefined;
			dotColor = undefined;
			classes = undefined;
		}

		return { fuelPrice, dotColor };
	}

	watch(
		[() => isServiceGasStationPOI, () => $searchParams],
		([_isServiceGasStationPOI, searchParams]) => {
			if (_isServiceGasStationPOI) {
				onGasStationFilterChange(searchParams?.filters?.GAS_STATION_TYPE as FuelType[] | undefined);
			}
		},
	);

	const poiScheme = $derived.by(() => {
		if (isAnyPoiPartner(poi)) {
			return;
		}

		if (poi.__typename === 'ServiceRestStopPOI' && poi.isFamilyFriendly) {
			return 'restSponsored';
		}

		return mapPoiTypeWithProps[poi.__typename].scheme;
	});
	const poiRecommended = $derived(
		!isAnyPoiPartner(poi) && mapPoiTypeWithProps[poi.__typename].recommended,
	);

	function shouldDisplayAccommodationNotAvailableDash(
		poi: AccommodationPoiData | AccommodationMichelinPoiData,
	): poi is (AccommodationPoiData | AccommodationMichelinPoiData) & {
		startingPrice: Price;
	} {
		const notAvailable = poi.isAvailable === false;
		const dateIsSpecified = !!$searchParams.filters?.ACCOMMODATION_AVAILABILITY_DATE_RANGE;
		return notAvailable && dateIsSpecified;
	}

	function shouldDisplayAccommodationStartingPrice(
		poi: AccommodationPoiData | AccommodationMichelinPoiData,
	): poi is (AccommodationPoiData | AccommodationMichelinPoiData) & {
		startingPrice: Price;
	} {
		const dateIsSpecified = !!$searchParams.filters?.ACCOMMODATION_AVAILABILITY_DATE_RANGE;
		const hasStartingPrice = !!poi.startingPrice;
		const hasPriceCategory = !!poi.priceCategory;
		const hasKeys = isAccommodationMichelinPoi(poi) && !!poi.distinctionStars;
		return hasStartingPrice && (dateIsSpecified || (!hasPriceCategory && !hasKeys));
	}

	const sponsoredPoiConfig = $derived(getSponsoredPoiCategoryConfigByPoi(poi));
	const isSponsoredPoi = $derived(isAnyPoiPartner(poi));
	const colorStyles = $derived.by(() => {
		if (!isHovered && !isSelectedLocation && isSponsoredPoi) {
			return sponsoredPoiConfig?.marker.style;
		}

		if (isHovered || isSelectedLocation) {
			return { 'background-color': 'var(--color-coloredneutral-strongest)', 'border-color': 'var(--color-coloredneutral-strongest)' };
		}

		return undefined;
	});

	function handleMouseEnter() {
		setHoveredPoiId(poi.id);

		if (!$isMobile) {
			setPopupState({ activePoiId: poi.id, open: true, poi });
		}
	}

	const debouncedHandleMouseLeave = debounce(DEFAULT_DEBOUNCE_TIME, () => {
		if (getPopupState().activePoiId !== $hoveredPoiId) {
			setPopupState({ activePoiId: undefined, open: false, poi: undefined });
		}
	});

	function setHoveredPoiId(poiId: string | null) {
		$hoveredPoiId = poiId;
	}

	function handleMouseLeave() {
		setHoveredPoiId(null);
		if (!$isMobile) {
			debouncedHandleMouseLeave();
		}
	}
</script>

{#snippet MarkerIcons(repeatIconsProps: ComponentProps<typeof RepeatIcons>)}
	<RepeatIcons {...repeatIconsProps} />
{/snippet}

{#snippet MarkerIcon(iconProps: ComponentProps<typeof Icon>)}
	<Icon {...iconProps} />
{/snippet}

<MarkerTemplate
	scheme={poiScheme}
	recommended={poiRecommended}
	class={[
		isSponsoredPoi ? 'px-0!' : classes,
		(isHovered || isSelectedLocation) && 'poi-marker-with-style bg-coloredneutral-strongest! border-coloredneutral-strongest! hovered',
		(hasPopupOpen && isHovered) && 'poi-marker-without-animation',
	]}
	dot={detailedView ? dotColor : undefined}
	imgUrl={isSponsoredPoi ? sponsoredPoiConfig?.marker.imgUrl : undefined}
	imgSize={isSponsoredPoi ? sponsoredPoiConfig?.marker.imgSize : undefined}
	sponsor={poi.__typename === 'ServiceRestStopPOI' && poi.isFamilyFriendly ? 'kleber' : undefined}
	{colorStyles}
	{isHovered}
	{iconTag}
	{tag}
	onmouseenter={handleMouseEnter}
	onmouseleave={handleMouseLeave}
>

	{#if isServiceGasStationPOI && detailedView && fuelPrice?.amount}
		{formatPrice({
			amount: fuelPrice.amount,
			currency: fuelPrice.currency,
			language: getLanguageCode(),
			digits: 3,
		})}
	{:else if isAnyAccommodationPoi(poi) && detailedView}
		{#if shouldDisplayAccommodationNotAvailableDash(poi)}
			{@render MarkerIcon({
				icon: 'remove',
				tag: iconTag,
			})}
		{:else if shouldDisplayAccommodationStartingPrice(poi)}
			{formatPrice({
				amount: poi.startingPrice.amount,
				currency: poi.startingPrice.currency,
				language: getLanguageCode(),
			})}
		{:else if isAccommodationMichelinPoi(poi) && !!poi.distinctionStars}
			{@render MarkerIcons({
				icon: 'michelin-key',
				amount: poi.distinctionStars,
				style: 'distinction',
				tag: iconTag,
				iconTag,
			})}
		{:else if !!poi.priceCategory}
			{@render MarkerIcons({
				icon: `currency-${poi.priceCategory.currency}`,
				amount: poi.priceCategory.count,
				style: 'currency',
				tag: iconTag,
				iconTag,
			})}
		{:else}
			{@render MarkerIcon({
				icon: 'hotel',
				tag: iconTag,
			})}
		{/if}
	{:else if poi.__typename === 'RestaurantMichelinPOI' && detailedView && poi.distinctionStars}
		{@render MarkerIcons({
			icon: 'michelin-star',
			amount: poi.distinctionStars,
			style: 'distinction',
			tag: iconTag,
			iconTag,
		})}
	{:else if poi.__typename === 'RestaurantMichelinPOI' && detailedView && poi.bibGourmand}
		{@render MarkerIcon({
			icon: 'bib-gourmand',
			tag: iconTag,
		})}
	{:else if poi.__typename === 'TourismPOI' && detailedView && poi.distinctionStars}
		{@render MarkerIcons({
			icon: 'tourism-star',
			amount: poi.distinctionStars,
			style: 'distinction',
			tag: iconTag,
			iconTag,
		})}
	{:else if poi.__typename === 'ParkingPOI' && poi.redirectUrl}
		{@render MarkerIcon({
			icon: 'parking-bookable',
			class: 'size-5',
			tag: iconTag,
		})}
	{:else if !isAnyPoiPartner(poi)}
		{@render MarkerIcon({
			icon: mapPoiTypeWithProps[poi.__typename].icon,
			class: 'size-5',
			tag: iconTag,
		})}
	{/if}
</MarkerTemplate>
